/**
 * スポットに紐付く場所（Place）の差し替え
 *
 * 一括登録では名前だけで Google を引くため別施設に当たることがある。
 * ここで引き直して正しい場所に付け替える。押した時点で即反映される。
 * スポットの表示名も場所に追従するので、編集シートでの場所の変更口はここだけ。
 */

import { Loader2, MapPin, Search } from 'lucide-react'
import { useEffect, useState } from 'react'
import { toast } from 'sonner'

import { getItemPlace, linkItemPlace, searchRallyPlaces } from '@/api/services/stamp-rally'
import type { ItemPlaceResult, LinkedPlace, PlaceSearchResult, StampRallyItem } from '@/api/types/stamp-rally'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import type { Coords } from '@/hooks/useCurrentPosition'
import { useSubmitLock } from '@/hooks/useSubmitLock'
import { formatDistanceMeters } from '@/lib/activity-format-utils'
import { apiErrorMessage } from '@/lib/api-error'

type Props = {
  item: StampRallyItem
  origin?: Coords | null
  /** 連携が変わったら一覧と地図を取り直す。表示名も変わるので更新後の item を渡す */
  onChanged: (result: ItemPlaceResult) => void
}

export const ItemPlaceSection = ({ item, origin, onChanged }: Props) => {
  const [place, setPlace] = useState<LinkedPlace | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const { isSubmitting: isSaving, run } = useSubmitLock()
  const [keyword, setKeyword] = useState('')
  const [results, setResults] = useState<PlaceSearchResult[]>([])
  const [isSearching, setIsSearching] = useState(false)

  useEffect(() => {
    const controller = new AbortController()
    setIsLoading(true)
    setResults([])
    setKeyword('')
    getItemPlace(item.id, controller.signal)
      .then(setPlace)
      .catch(() => undefined)
      .finally(() => {
        if (!controller.signal.aborted) setIsLoading(false)
      })
    return () => controller.abort()
  }, [item.id])

  // 従量課金なので入力中は引かず、確定した文字列でだけ検索する
  const handleSearch = async () => {
    const query = keyword.trim() || item.name
    setIsSearching(true)
    try {
      const found = await searchRallyPlaces(query, origin)
      setResults(found)
      if (found.length === 0) toast.info('見つかりませんでした')
    } catch {
      toast.error('場所の検索に失敗しました')
    } finally {
      setIsSearching(false)
    }
  }

  const handlePick = async (result: PlaceSearchResult) => {
    if (!result.google_place_id) {
      toast.error('この候補は紐付けできません')
      return
    }
    await run(async () => {
      try {
        const updated = await linkItemPlace(item.id, result)
        setPlace(updated.place)
        setResults([])
        toast.success('場所を差し替えました')
        onChanged(updated)
      } catch (error) {
        toast.error(apiErrorMessage(error, '場所を差し替えられませんでした'))
      }
    })
  }

  return (
    <div className="space-y-2 rounded-xl border bg-card p-3">
      <Label>場所</Label>

      {isLoading ? (
        <Loader2 className="size-4 animate-spin text-muted-foreground" />
      ) : place ? (
        <div className="flex items-start gap-2">
          <MapPin className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm">{place.name}</p>
            {place.address && <p className="truncate text-xs text-muted-foreground">{place.address}</p>}
          </div>
        </div>
      ) : (
        <p className="text-xs text-muted-foreground">
          未連携です。連携すると訪問履歴からスタンプを提案できるようになります。
        </p>
      )}

      <p className="text-xs text-muted-foreground">
        検索して選ぶと、スポットの名前と位置もこの場所に合わせて変わります。
      </p>

      <div className="flex gap-2">
        <Input value={keyword} onChange={(e) => setKeyword(e.target.value)} placeholder={item.name} maxLength={200} />
        <Button
          variant="outline"
          size="icon"
          className="shrink-0"
          disabled={isSearching || isSaving}
          onClick={handleSearch}
        >
          {isSearching ? <Loader2 className="size-4 animate-spin" /> : <Search className="size-4" />}
        </Button>
      </div>

      {results.length > 0 && (
        <div className="overflow-hidden rounded-lg border">
          {results.map((result) => (
            <button
              key={result.google_place_id ?? `${result.latitude},${result.longitude}`}
              disabled={isSaving}
              onClick={() => void handlePick(result)}
              className="flex w-full items-start gap-2 border-b border-border/50 px-3! py-2! text-left last:border-b-0"
            >
              <MapPin className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm">{result.name}</p>
                {result.address && <p className="truncate text-xs text-muted-foreground">{result.address}</p>}
              </div>
              {result.distance_m !== null && (
                <span className="shrink-0 text-xs tabular-nums text-muted-foreground">
                  {formatDistanceMeters(result.distance_m)}
                </span>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
